import _ from 'lodash';
import FetchService from './base.fetch';
import mElectron from '../../util/electron';
import { genId, readFile, getUrlParams } from '../../util';
import CONSTANT from '../../constant';
import StoreService from '../../services/api/store';
import Api from '../../services/api';
import Config from '../../config';
import path from 'path';
import MscratchService from '../mscratch';
import projectModel from '../../model/project.model';
import {getInternational} from '../../util/env';

/**
 * 作品管理
 *
 * @export
 * @class ProjectService
 */
class ProjectService {

    /**
     * Creates an instance of ProjectService.
     * @memberof ProjectService
     */
    constructor() {
        this.domain = FetchService.domainconfig.cloud[getInternational()];
        this.preProjectDataString = null;
        this.projectList = [];
        const header = {
            'clientdevice': `system/${window.navigator.userAgent};version/${Config.appVersion}`
        };
        const apisConfig = {
            // 获取用户云端作品列表
            get_cloud_projects: {
                method: 'post',
                url: 'cloud/pro/list'
            },
            // 获取某几个云端作品
            get_some_cloud_projects: {
                method: 'post',
                url: 'getprojectbycpids'
            },
            // 修改作品名
            rename_project: {
                method: 'post',
                url: 'cloud/pro/edit'
            },
            // 删除作品
            delete_project: {
                method: 'post',
                url: 'cloud/pro/del'
            },
            // 分享作品
            share_project: {
                method: 'post',
                url: 'project/publish'
            },
            // 取消分享
            cancel_share_project: {
                method: 'post',
                url: 'project/unpublish'
            },
            // 生成UrlToken（跳转社区）
            get_url_token: {
                domain: FetchService.domainconfig.passport2,
                method: 'post',
                url: 'v1/user/geturltoken',
                header
            },
            // 标记项目为第三方平台的项目
            set_project_as_third_platform: {
                method: 'post',
                url: 'project/ThirdPlatform'
            }
        };

        FetchService.configToRequestAction.call(this, apisConfig, FetchService);
    }

    syncWebList(list) {
        this.projectList = list;
    }

    /**
     * 同步我的作品
     *
     * @param {any} data
     * @returns
     */
    syncMyList(cloudList, localList) {
        if (!_.isArray(cloudList)) {
            return localList;
        }
        const PROJECT_STATUS = CONSTANT.PROJECT_STATUS;
        cloudList = cloudList.map((cloudItem) => {
            const { commitId, coverImage, createdAt, id, projectName, uid, isShare } = cloudItem;
            const _id = genId();
            return {
                id: _id,
                userId: String(uid),
                projectId: id,
                commitId,
                projectName,
                localName: _id,
                isDeleted: false,
                isShare,
                syncStatus: CONSTANT.PROJECT_STATUS['WILL_DOWNLOAD'],
                createdAt,
                coverImage
            };
        });
        const cloudListIds = cloudList.map((c) => c.projectId);
        localList = localList.map((localItem) => {
            if (localItem.projectId && _.indexOf(cloudListIds, localItem.projectId) < 0 &&
                _.indexOf([PROJECT_STATUS['NORMAL'], PROJECT_STATUS['WILL_DOWNLOAD'], PROJECT_STATUS['WILL_DELETE'], PROJECT_STATUS['WILL_UPLOAD_RECORD']], localItem.syncStatus) > -1
            ) {
                const createdAt = Date.parse(new Date()) / 1000;
                return { ...localItem, isDeleted: true, createdAt, syncStatus: PROJECT_STATUS['NORMAL'] };
            } else {
                return localItem;
            }
        });
        const data = localList.concat(cloudList);
        let keyObj = {};
        let list = [];
        data.forEach((d) => {
            if (!d.projectId) {
                list.push(d);
                return;
            }
            if (!_.has(keyObj, d.projectId)) {
                keyObj[d.projectId] = d;
            } else {
                const { commitId, coverImage, createdAt, projectName, isShare } = d;
                if (d.createdAt > keyObj[d.projectId].createdAt) {
                    keyObj[d.projectId] = { ...keyObj[d.projectId], commitId, coverImage, createdAt, projectName, isShare, isDeleted: false, syncStatus: CONSTANT.PROJECT_STATUS['WILL_DOWNLOAD'] };
                } else if (d.createdAt === keyObj[d.projectId].createdAt) {
                    keyObj[d.projectId] = { ...keyObj[d.projectId], commitId, createdAt, projectName, isShare, isDeleted: false };
                }
            }
        });
        for (let key in keyObj) {
            list.push(keyObj[key]);
        }
        return list;
    }

    /**
     * 读取本地所有作品列表（Array to Object）
     *
     * @param {any} data
     * @returns
     */
    async getLocalList() {
        const filePath = path.join(Config.userProjectFilePath, Config.userProjectFileName);
        const res = await mElectron.read(filePath);
        if (res && res.data.length > 0) {
            const data = res.data;
            const localList = {};
            data.forEach((d) => {
                if (localList[d.userId]) {
                    localList[d.userId].push(d);
                } else {
                    localList[d.userId] = [];
                    localList[d.userId].push(d);
                }
            });
            return localList;
        } else {
            return {};
        }
    }

    /**
     * 设置本地所有作品列表（Object to Array）
     *
     * @param {any} data
     * @returns
     */
    setLocalList(localList) {
        const list = [];
        for (const key in localList) {
            if (localList.hasOwnProperty(key)) {
                localList[key].forEach((item) => {
                    list.push(item);
                });
            }
        }
        return list;
    }

    /**
     * 读取本地我的作品列表
     *
     * @param {any} data
     * @returns
     */
    getMyLocalList(userId, localList) {
        if (userId && userId !== 'null') {
            if (localList['null']) {
                localList['null'] = localList['null'].map((l) => { return { ...l, userId }; });
                localList[userId] = (localList[userId] || []).concat(localList['null']);
                delete localList['null'];
            }
            return localList[userId] || [];
        } else if (localList['null']) {
            return localList['null'] || [];
        } else {
            return [];
        }
    }

    /**
     * 获取不重复的项目名
     *
     * @param {any} name
     * @param {any} list
     * @returns
     */
    getNewProjectName(name, list) {
        list = list || [];
        name = _.trim(name).length > 48
            ? name.substring(0, 48)
            : name;
        let names = list.filter((l) => !l.isDeleted).map((l) => l.projectName);

        if (_.indexOf(names, name) > -1) {
            name = name.replace(/\(\d+\)$/, '');
            const _name = name.replace('(', '\\(').replace(')', '\\)');
            let max = 0;
            names.forEach((n) => {
                const arr = n.match(new RegExp(_name + '\\((\\d+)\\)$'));
                if (arr && arr[1] > max) {
                    max = Number(arr[1]);
                }
            });
            return name + '(' + ((max > 98 ? 98 : max) + 1) + ')';
        } else {
            return name;
        }
    }

    /**
     * 查找列表是否有待上传项
     *
     * @param {any} projects
     * @returns
     */
    hasUnWillUpload(projects) {
        const PROJECT_STATUS = CONSTANT.PROJECT_STATUS;
        const _projects = projects.filter((p) => {
            return _.indexOf([PROJECT_STATUS['WILL_UPLOAD_RECORD'], PROJECT_STATUS['WILL_UPLOAD_FILE'], PROJECT_STATUS['WILL_UPLOAD_ALL']], p.syncStatus) > -1;
        });
        if (_projects.length > 0) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * 写用户本地作品列表JSON
     *
     * @param {any} data 写的数据
     */
    write(data, method) {
        if (!_.isArray(data)) {
            data = this.setLocalList(data);
        }
        // 异常数据检测处理
        data = this.checkErr(data);
        return new Promise((resolve, reject) => {
            this.projectList = this.projectList.concat(data);
            console.log({
                data,
                write: path.join(Config.userProjectFilePath, Config.userProjectFileName)
            });
            const projects = _.cloneDeep(this.projectList);
            const config = [];
            config.push({
                opts: {
                    path: path.join(Config.userProjectFilePath, Config.userProjectFileName),
                    isAppend: false,
                    sync: true,
                    method
                },
                data: projects
            });
            try {
                this.projectList = [];
                mElectron.writeJson(config).then(res => {
                    resolve(res);
                }, rej => {
                    reject(rej);
                });
            } catch (error) {
                this.projectList = projects.concat(this.projectList);
                reject(error);
            }
        });
    }

    /**
     * 数据异常检测处理
     *
     * @param {any} data 写的数据
     */
    checkErr(data) {
        // 查詢沒有id的情况
        const _data = data.filter((d) => !!d.id);
        if (_data.length !== data.length) {
            console.log('数据错误！！有没id的数据');
            data = _data;
        }
        // 查詢projectId的情況
        let keyObj = {};
        let res = [];
        data.forEach((d) => {
            if (d.projectId === '') {
                res.push(d);
                return;
            }
            if (!_.has(keyObj, d.projectId)) {
                keyObj[d.projectId] = d;
            } else {
                console.log('数据错误！！有重復projectid的数据', d.projectId);
                if (d.createdAt > keyObj[d.projectId].createdAt) {
                    keyObj[d.projectId] = d;
                }
            }
        });
        for (let key in keyObj) {
            res.push(keyObj[key]);
        }
        return res;
    }

    /**
     * 保存作品封面
     *
     * @param {any} data 写的数据
     */
    async uploadCoverImage(data, localName) {
        try {
            // 获取封面上传令牌
            const config = await Api.OssService.uploadImgToken({
                '_HEADER_': { 'utoken': StoreService.get(CONSTANT.USER_ACCESS_TOKEN_KEY), 'pid': 2 }
            });
            if (config) {
                const file = new File(Array(data), localName);
                const res = await Api.OssService.uploadCoverImg(config, file);
                if (res && res.res && res.res.status === 200) {
                    console.log('上传封面成功！！');
                    const coverImage = `https://${config.bucket}.${config.region}.aliyuncs.com/${config.commitId}`;
                    return coverImage;
                } else {
                    console.log('上传封面失败！！');
                    return '';
                }
            }
        } catch (err) {
            console.log('上传封面失败！！', err);
            return '';
        }
    }

    sortBy(data, method) {
        switch (method) {
            case 'createdAtReverse':
                return data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            default:
                return data;
        }
    }

    search(data, text) {
        return _.filter(data, (item) => item.projectName.toLowerCase().indexOf(_.trim(text.toLowerCase())) !== -1);
    }

    searchExample(data, text, language) {
        return _.filter(data, (item) => item.name[language].toLowerCase().indexOf(_.trim(text.toLowerCase())) !== -1);
    }

    saveProjectData(id, data) {
        if(!id) return;
        for (let item of this.projectList) {
            if (item.id == id) {
                item.data = data;
            }
        }
    }

    getProject(id) {
        let data = null;
        for (let item of this.projectList) {
            if (item.id == id) {
                data = item;
                return data;
            }
        }
        return data;
    }

    export() {
        MscratchService.export();
    }

    exportFromWeb(project) {
        MscratchService.export(null, null, project);
    }

    /**
     * 获取远程文件的基本信息
     *
     * @param {number} projectId project id.
     * @returns {Object}
     * {
     *    id: 项目 Id,
     *    projectName: 项目名称,
     *    coverImage: 项目图片,
     *    uid: 创建者信息
     *    commitId: 文件下载的 Id
     * }
     */
    async fetchProjectBaseInfoById(projectId) {
        let pids = [projectId];
        let data = await Api.ProjectService.get_some_cloud_projects({
            projectIds: pids
        });
        let info = data && data.projectList && data.projectList[0];
        if (JSON.stringify(info) === '{}') return null;
        return info;
    }

    /* 查询项目在云端是否存在或者可访问，当前用户不可访问也认为是不存在 */
    async isProjectCanBeVisited(projectId) {
        if(!projectId) return false;
        let info = await this.fetchProjectBaseInfoById(projectId);
        if(info === null) {
            console.log('该项目不存在');
            projectModel.resetProjectState();
            return false;
        }
        return true;
    }

    /**
     * 获取指定项目的文件数据
     *
     * @returns {ArrayBuffer}
     */
    async fetchProjectData(commitId, projectName, id, projectId, classToken) {
        try {
            const res = await Api.OssService.downloadToken({
                'utoken': StoreService.get(CONSTANT.USER_ACCESS_TOKEN_KEY),
                'pid': 1,
                'cloudProjectId': projectId,
                'classToken': classToken || ''
            });
            if (res) {
                // 设置该作品是否为分享到第三方平台作品
                projectModel.setProjectCloudThirdPlatform(res.isForThirdPlatform);
                projectModel.setProjectPlatformType(projectId);
                res.commitId = commitId;
                res.localName = projectName || 'project';
                let filePath = Api.OssService.fetchFileUrl(res);
                let projectData = await readFile(filePath);
                this.saveProjectData(id, projectData);
                return projectData;
            } else {
                throw new Error('get oss download token failed.');
            }
        } catch(err) {
            console.log('获取指定文件失败：', err);
        }
    }

    async loadRemoteProjectFromUrl(url) {
        let params = getUrlParams(url) || {};
        console.log(params);
        let projectId = params.cloudProjectId || '';
        let classToken = params.classToken || '';
        if(!projectId) return null;
        let info = await this.fetchProjectBaseInfoById(projectId);
        if (info) {
            return await this.fetchProjectData(info.commitId, info.projectName, info.id, projectId, classToken);
        }
    }

    getCorrectId(projectId) {
        let params = getUrlParams() || {};
        const id = params.cloudProjectId || '';
        if (id) {
            projectId = id;
        }
        return projectId;
    }

    hasShared(projects, projectId) {
        let hasPublished = false;
        const item = projects[_.indexOf(projects.map(l => l.id === parseInt(projectId)), true)];
        if (projectId && item && item.isShare) {
            hasPublished = true;
        }
        return hasPublished;
    }

    isSelfProject(id) {
        const projectId = this.getCorrectId(id);
        if (!projectId || (projectId && this.getProject(projectId))) {
            return true;
        } else {
            return false;
        }
    }

    // 提交项目到后台，可通过外链打开，但是不在社区首页显示，也不标记为发布状态
    async saveAsThirdPlatformProject(projectId) {
        console.log('保存项目为第三方平台项目, ', projectId);
        await Api.ProjectService.set_project_as_third_platform({
            'cloudProjectId': projectId,
            '_HEADER_': { 'utoken': StoreService.get(CONSTANT.USER_ACCESS_TOKEN_KEY) }
        }).then(res => {
            if(typeof res.code === 'undefined') {
                projectModel.setProjectCloudThirdPlatform(true);
            }
        });
    }
}

let projectService = new ProjectService();
window.projectService = projectService;

export default projectService;